import { useMemo, useState } from "react";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  DEFAULT_ELEMENT_ANIMATION,
  ELEMENT_EFFECTS,
  ELEMENT_TRIGGERS,
  buildAnimationSchedule,
  getAnimatedElements,
} from "../../../canvas/design/elementAnimations";
import { getRenderableElements } from "../../../canvas/design/slideDesign";

const plain = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const describeElement = (el) => {
  if (!el) return "Element";
  if (el.type === "TEXT") {
    const text = plain(el.content);
    return text ? `“${text.slice(0, 38)}${text.length > 38 ? "…" : ""}”` : "Text box";
  }
  if (el.type === "SLIDE") return "Histology slide";
  if (el.type === "3D_MODEL") return "3D anatomy model";
  if (el.type === "VIDEO") return "Video";
  if (el.type === "IMAGE") return "Image";
  return el.type || "Element";
};

const typeBadge = (el) =>
  el.type === "TEXT" ? "T" : el.type === "SLIDE" ? "H" : el.type === "3D_MODEL" ? "3D" : el.type === "VIDEO" ? "V" : "I";

export default function AnimationTab({ elements, onUpdateElements, onSelectElement, onPreview, onPresentHere }) {
  const [openId, setOpenId] = useState(null);
  const items = useMemo(
    () => getRenderableElements(elements).filter((el) => el.id && !el.isBackground),
    [elements]
  );
  const animated = useMemo(() => getAnimatedElements(items), [items]);
  const { schedule, steps } = useMemo(() => buildAnimationSchedule(items), [items]);
  const still = items.filter((el) => !el.animation?.effect);

  // Total length of the longest step, for the timeline bars.
  const stepLength = useMemo(() => {
    const lengths = new Map();
    schedule.forEach((e) => {
      lengths.set(e.step, Math.max(lengths.get(e.step) || 0, e.start + e.delay + e.duration));
    });
    return lengths;
  }, [schedule]);

  const nextOrder = () =>
    animated.reduce((max, el) => Math.max(max, Number(el.animation?.order) || 0), -1) + 1;

  const enable = (el) => {
    onUpdateElements([
      { id: el.id, patch: { animation: { ...DEFAULT_ELEMENT_ANIMATION, order: nextOrder() } } },
    ]);
    setOpenId(el.id);
  };

  const patchAnimation = (el, patch) =>
    onUpdateElements([{ id: el.id, patch: { animation: { ...el.animation, ...patch } } }]);

  const remove = (el) => onUpdateElements([{ id: el.id, patch: { animation: null } }]);

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= animated.length) return;
    const reordered = [...animated];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onUpdateElements(
      reordered.map((el, i) => ({ id: el.id, patch: { animation: { ...el.animation, order: i } } }))
    );
  };

  const animateAll = (trigger) => {
    // Titles first, then the rest top-to-bottom: a natural reading order.
    const ordered = [...items].sort(
      (a, b) => (Number(a.position?.top) || 0) - (Number(b.position?.top) || 0) || (Number(a.position?.left) || 0) - (Number(b.position?.left) || 0)
    );
    onUpdateElements(
      ordered.map((el, i) => ({
        id: el.id,
        patch: {
          animation: {
            ...DEFAULT_ELEMENT_ANIMATION,
            effect: el.type === "TEXT" ? "riseUp" : el.type === "SLIDE" || el.type === "3D_MODEL" ? "fadeIn" : "zoomIn",
            trigger: i === 0 ? "enter" : trigger,
            order: i,
          },
        },
      }))
    );
  };

  const clearAll = () => onUpdateElements(animated.map((el) => ({ id: el.id, patch: { animation: null } })));

  return (
    <>
      <p className="cine-studio-sub">
        Build the slide one idea at a time. Defaults are short and gentle, suited to teaching.
      </p>
      <div className="cine-row" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <button type="button" className="cine-btn-secondary" onClick={() => animateAll("click")} disabled={!items.length}>
          Reveal one by one
        </button>
        <button type="button" className="cine-btn-secondary" onClick={() => animateAll("after")} disabled={!items.length}>
          Auto sequence
        </button>
        {animated.length ? (
          <button type="button" className="cine-btn-secondary" onClick={clearAll}>
            Clear
          </button>
        ) : null}
      </div>

      <div className="cine-row" style={{ marginBottom: 6 }}>
        <span className="cine-studio-label cine-grow" style={{ margin: 0 }}>
          Timeline · {steps} click{steps === 1 ? "" : "s"}
        </span>
        <button type="button" className="cine-btn-secondary" onClick={onPreview} disabled={!animated.length}>
          ▶ Preview
        </button>
        <button type="button" className="cine-btn-secondary" onClick={onPresentHere}>
          Present
        </button>
      </div>

      {animated.length ? (
        <div className="cine-timeline">
          {animated.map((el, index) => {
            const a = el.animation;
            const entry = schedule.get(el.id);
            const total = stepLength.get(entry?.step) || 1;
            const open = openId === el.id;
            return (
              <div
                key={el.id}
                className={`cine-timeline-item${open ? " is-selected" : ""}`}
                onClick={() => {
                  setOpenId(open ? null : el.id);
                  onSelectElement(el.id);
                }}
              >
                <div className="cine-timeline-head">
                  <span className="cine-timeline-index" title={entry?.step ? `Click ${entry.step}` : "On slide enter"}>
                    {entry?.step || "▸"}
                  </span>
                  <span className="cine-timeline-name">
                    <small style={{ opacity: 0.5, marginRight: 4 }}>{typeBadge(el)}</small>
                    {describeElement(el)}
                  </span>
                  <button
                    type="button"
                    className="cine-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      move(index, -1);
                    }}
                    disabled={index === 0}
                    aria-label="Move earlier"
                  >
                    <ArrowUpwardRoundedIcon fontSize="inherit" />
                  </button>
                  <button
                    type="button"
                    className="cine-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      move(index, 1);
                    }}
                    disabled={index === animated.length - 1}
                    aria-label="Move later"
                  >
                    <ArrowDownwardRoundedIcon fontSize="inherit" />
                  </button>
                  <button
                    type="button"
                    className="cine-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(el);
                    }}
                    aria-label="Remove animation"
                  >
                    <CloseRoundedIcon fontSize="inherit" />
                  </button>
                </div>
                <div className="cine-timeline-bar" aria-hidden="true">
                  <span
                    style={{
                      left: `${((entry?.start || 0) + (entry?.delay || 0)) / total * 100}%`,
                      width: `${Math.max(4, ((entry?.duration || 0) / total) * 100)}%`,
                    }}
                  />
                </div>
                {open ? (
                  <div className="cine-timeline-grid" onClick={(e) => e.stopPropagation()}>
                    <select
                      className="cine-field"
                      value={a.effect}
                      onChange={(e) => patchAnimation(el, { effect: e.target.value })}
                      aria-label="Effect"
                    >
                      {ELEMENT_EFFECTS.map((fx) => (
                        <option key={fx.id} value={fx.id}>
                          {fx.label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="cine-field"
                      value={a.trigger || "click"}
                      onChange={(e) => patchAnimation(el, { trigger: e.target.value })}
                      aria-label="Start"
                    >
                      {ELEMENT_TRIGGERS.map((tr) => (
                        <option key={tr.id} value={tr.id}>
                          {tr.label}
                        </option>
                      ))}
                    </select>
                    <label style={{ fontSize: 11.5 }}>
                      Delay (ms)
                      <input
                        className="cine-field"
                        type="number"
                        min={0}
                        max={5000}
                        step={100}
                        value={a.delay ?? 0}
                        onChange={(e) => patchAnimation(el, { delay: Math.max(0, Number(e.target.value) || 0) })}
                      />
                    </label>
                    <label style={{ fontSize: 11.5 }}>
                      Duration (ms)
                      <input
                        className="cine-field"
                        type="number"
                        min={150}
                        max={4000}
                        step={50}
                        value={a.duration ?? DEFAULT_ELEMENT_ANIMATION.duration}
                        onChange={(e) =>
                          patchAnimation(el, { duration: Math.min(4000, Math.max(150, Number(e.target.value) || 600)) })
                        }
                      />
                    </label>
                    <label style={{ fontSize: 11.5, gridColumn: "1 / -1" }} title="Give the same Morph ID to an element on the next slide to morph between them">
                      Morph ID (optional)
                      <input
                        className="cine-field"
                        defaultValue={el.morphKey || ""}
                        placeholder="e.g. heart-overview"
                        onBlur={(e) => {
                          const next = e.target.value.trim() || undefined;
                          if (next !== (el.morphKey || undefined)) {
                            onUpdateElements([{ id: el.id, patch: { morphKey: next } }]);
                          }
                        }}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cine-empty">No animations on this slide yet. Add one below or use “Reveal one by one”.</div>
      )}

      {still.length ? (
        <>
          <span className="cine-studio-label">Add animation</span>
          <div className="cine-timeline">
            {still.map((el) => (
              <div key={el.id} className="cine-timeline-item" style={{ padding: "6px 10px" }}>
                <div className="cine-timeline-head">
                  <span className="cine-timeline-name" onClick={() => onSelectElement(el.id)} style={{ cursor: "pointer" }}>
                    <small style={{ opacity: 0.5, marginRight: 4 }}>{typeBadge(el)}</small>
                    {describeElement(el)}
                  </span>
                  <button type="button" className="cine-chip" onClick={() => enable(el)}>
                    + Animate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
      <p className="cine-studio-sub" style={{ marginTop: 12 }}>
        Histology slides: use the focus button on the viewer to save zoom stops, and the list button
        to reveal labels one by one. 3D models: use the camera button to save viewpoints. Each stop
        becomes a click in the presentation, after the element builds.
      </p>
    </>
  );
}
